import React, { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import WarningModal from "../ui/warningmodel";

export default function DateLabelInput({
  label,
  name,
  value,
  onChange,
  onBlur,
  onFocus,
  required = false,
  placeholder = "",
  error,
  disabled = false,
}) {
  const [inputValue, setInputValue] = useState("");
  const [warningVisible, setWarningVisible] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");

  // Sync when parent value changes
  useEffect(() => {
    if (!value) {
      setInputValue("");
      return;
    }
    const dateObj = new Date(value);
    if (!isNaN(dateObj)) {
      const dd = String(dateObj.getDate()).padStart(2, "0");
      const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
      const yyyy = dateObj.getFullYear();
      setInputValue(`${dd}/${mm}/${yyyy}`);
    }
  }, [value]);

  // Calendar selection
  const handleDateChange = (date) => {
    if (!date) return;
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();
    const displayValue = `${dd}/${mm}/${yyyy}`;
    const isoValue = `${yyyy}-${mm}-${dd}`;
    setInputValue(displayValue);
    onChange?.({ target: { name, value: isoValue, displayValue } });
  };

  // Manual typing — respect user-typed slashes, but still insert slashes for continuous digits
  const handleManualInput = (e) => {
    let raw = e?.target?.value ?? "";
    if (typeof raw !== "string") return;

    const userHasSlash = raw.includes("/");

    if (userHasSlash) {
      const parts = raw.split("/").map((p) =>
        p.replace(/\D/g, "").slice(0, p === raw.split("/")[0] ? 2 : 4)
      );
      parts[0] = (parts[0] || "").slice(0, 2);
      if (parts.length > 1) parts[1] = (parts[1] || "").slice(0, 2);
      if (parts.length > 2) parts[2] = (parts[2] || "").slice(0, 4);
      const joined = [parts[0], parts[1], parts[2]]
        .filter((p, i) => {
          if (i < 2) return p !== "";
          return p !== "";
        })
        .join("/");
      setInputValue(joined);
    } else {
      const digits = raw.replace(/\D/g, "");
      if (digits.length > 2 && digits.length <= 4)
        setInputValue(`${digits.slice(0, 2)}/${digits.slice(2)}`);
      else if (digits.length > 4)
        setInputValue(
          `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`
        );
      else setInputValue(digits);
    }
  };

  const handleBlur = (e) => {
    let val = inputValue.trim();
    if (!val) {
      onBlur?.(e);
      return;
    }

    const parts = val.split("/");
    let dd = (parts[0] || "").replace(/\D/g, "");
    let mm = (parts[1] || "").replace(/\D/g, "");
    let yyyy = (parts[2] || "").replace(/\D/g, "");

    // Auto pad single digits BEFORE validation
    if (dd.length === 1) dd = dd.padStart(2, "0");
    if (mm.length === 1) mm = mm.padStart(2, "0");

    const dayNum = parseInt(dd, 10);
    const monthNum = parseInt(mm, 10);

    // Only validate if day and month are present
    if (dd && mm) {
      if (dayNum < 1 || dayNum > 31 || monthNum < 1 || monthNum > 12) {
        setInputValue(""); // clear field
        onChange?.({ target: { name, value: "", displayValue: "" } });
        setWarningMessage("Invalid date! Day must be 1-31 and month 1-12.");
        setWarningVisible(true);
        onBlur?.(e);
        return;
      }
    }

    // Check if year is incomplete (not empty and not 4 digits)
    if (yyyy && yyyy.length !== 4) {
      setInputValue(""); // clear field
      onChange?.({ target: { name, value: "", displayValue: "" } });
      setWarningMessage("Please enter a complete 4-digit year.");
      setWarningVisible(true);
      return;
    }

    const normalized = [dd, mm, yyyy]
      .filter((p, i) => {
        if (i < 2) return p !== "";
        return p !== "";
      })
      .join("/");

    setInputValue(normalized);

    if (dd && mm && yyyy && yyyy.length === 4) {
      const date = new Date(`${yyyy}-${mm}-${dd}`);
      if (!isNaN(date)) {
        const isoValue = `${yyyy}-${mm}-${dd}`;
        onChange?.({
          target: { name, value: isoValue, displayValue: normalized },
        });
      }
    }

    onBlur?.(e);
  };


  const parsedSelectedDate = (() => {
    if (/^(\d{2})\/(\d{2})\/(\d{4})$/.test(inputValue)) {
      const [dd, mm, yyyy] = inputValue.split("/");
      const d = new Date(`${yyyy}-${mm}-${dd}`);
      return isNaN(d) ? null : d;
    }
    return null;
  })();

  return (
    <>
      <style>{`
        .react-datepicker {
          font-family: inherit;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          box-shadow: 0 10px 25px rgba(0,0,0,0.1);
        }
        .react-datepicker__header {
          background-color: #1f2937;
          border-bottom: none;
          padding-top: 16px;
        }
        .react-datepicker__current-month {
          color: white;
          font-weight: 600;
        }
        .react-datepicker__day--selected {
          background-color: #1f2937 !important;
          color: white !important;
        }
      `}</style>

      <div className="relative w-full">
        {label && (
          <label
            htmlFor={name}
            className={`absolute left-2.5 -top-2 bg-white px-1 text-xs font-medium z-10 ${error ? "text-red-600" : "text-gray-700"
              }`}
          >
            {label}
            {required && <span className="text-red-500 ml-0.5">*</span>}
          </label>
        )}

        <div className="relative">
          <style>{`
    /* Make react-datepicker wrapper take full width */
    .react-datepicker-wrapper {
      display: inline-block;
      padding: 0;
      border: 0;
      width: 100%;
    }
  `}</style>

          <DatePicker
            id={name}
            selected={parsedSelectedDate}
            onChange={handleDateChange}
            onChangeRaw={handleManualInput}
            onBlur={handleBlur}
            onFocus={onFocus}
            dateFormat="dd/MM/yyyy"
            placeholderText={placeholder || "DD/MM/YYYY"}
            value={inputValue}
            disabled={disabled}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            showYearDropdown
            yearDropdownItemNumber={new Date().getFullYear() + 2 - 2000 + 1}
            scrollableYearDropdown
            minDate={new Date(2000, 0, 1)}
            maxDate={new Date(new Date().getFullYear() + 2, 11, 31)}
            renderCustomHeader={({
              date,
              changeYear,
              changeMonth,
              decreaseMonth,
              increaseMonth,
              prevMonthButtonDisabled,
              nextMonthButtonDisabled,
            }) => (
              <div className="flex items-center justify-between px-2">
                <button onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>
                  {"<"}
                </button>
                <select
                  value={date.getMonth()}
                  onChange={({ target: { value } }) => changeMonth(Number(value))}
                  className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  {Array.from({ length: 12 }, (_, i) => (
                    <option key={i} value={i}>
                      {new Date(0, i).toLocaleString("default", { month: "long" })}
                    </option>
                  ))}
                </select>

                <select
                  value={date.getFullYear()}
                  onChange={({ target: { value } }) => changeYear(Number(value))}
                  className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  {Array.from(
                    { length: new Date().getFullYear() + 2 - 2000 + 1 },
                    (_, i) => 2000 + i
                  ).map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
                <button onClick={increaseMonth} disabled={nextMonthButtonDisabled}>
                  {">"}
                </button>
              </div>
            )}
            className={`w-full border rounded h-11 px-3 py-2 text-sm text-gray-800 bg-white focus:outline-none ${error ? "border-red-500" : "border-gray-300"} ${disabled ? "opacity-60 cursor-not-allowed bg-gray-50" : "hover:border-gray-600 focus:border-gray-600"}`}
          />

        </div>

        {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
      </div>
      {warningVisible && (
        <WarningModal
          message={warningMessage}
          onClose={() => setWarningVisible(false)}
        />
      )}
    </>
  );
}